import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTargetAccumulatorSettlementModeData } from '@/store/targetAccumulator/selectors.ts';
import type { SettlementMode as SettlementModeType } from '@/store/targetAccumulator/state.ts';

const SettlementMode = ({ useShortLabel = false }: { useShortLabel?: boolean }) => {
  const tileId = useTargetAccumulatorTileId();
  const { settlementMode, settlementModeList, cashSettlementCurrency, cashSettlementCurrencyList } = useAppSelector(
    (state) => selectTargetAccumulatorSettlementModeData(state, tileId),
  );
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleSettlementMode = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Settlement mode changed',
      isDefined(event.target.value) ? event.target.value : 'cleared',
    );

    return checkTargetAccumulatorWithTradeCapture({
      settlementMode: event.target.value as SettlementModeType,
    });
  };

  const handleSettlementCurrency = (event: React.ChangeEvent<HTMLSelectElement>) => {
    return checkTargetAccumulatorWithTradeCapture({
      cashSettlementCurrency: event.target.value,
    });
  };

  return (
    <TargetAccumulatorFormField
      formattedMessageId={`targetAccumulator.form.settlementMode${useShortLabel ? '.short' : ''}`}
    >
      <div className="d-flex">
        <Form.Select
          value={settlementMode}
          onChange={handleSettlementMode}
          size="sm"
          data-e2e="settlement-mode-selector"
        >
          {settlementModeList?.map((mode) => (
            <option key={mode} value={mode}>
              <FormattedMessage id={`targetAccumulator.form.settlementMode.${mode}`} />
            </option>
          ))}
        </Form.Select>
        {settlementMode === 'Cash' && (
          <div className="w-50 ps-1">
            <Form.Select
              value={cashSettlementCurrency}
              onChange={handleSettlementCurrency}
              size="sm"
              data-e2e="settlement-currency-selector"
            >
              {cashSettlementCurrencyList?.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </Form.Select>
          </div>
        )}
      </div>
    </TargetAccumulatorFormField>
  );
};

export default SettlementMode;
